/**
 * Debounce a callback.
 */
function debounce(fn, wait = 300) {
  let timer;

  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();

      this.isPaused = false;
      this.marqueeFrame = null;
      this.marqueeResizeObserver = null;
      this.marqueeTrack = null;
      this.marqueeGroup = null;
      this.marqueeSlides = null;
      this.lastMarqueeWidth = null;

      this.moveLinksHandler = this.moveLinks.bind(this);
      this.moveLocalizationHandler = debounce(
        this.moveLocalization.bind(this)
      );
      this.pauseHandler = this.pauseSlider.bind(this);
      this.resumeHandler = this.resumeSlider.bind(this);
    }

    connectedCallback() {
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector(
        '.announcement__localization'
      );
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');

      this.bindEvents();
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener(
        'on:breakpoint-change',
        this.moveLinksHandler
      );

      window.removeEventListener(
        'on:breakpoint-change',
        this.moveLocalizationHandler
      );

      if (this.slider) {
        this.slider.removeEventListener(
          'mouseenter',
          this.pauseHandler
        );

        this.slider.removeEventListener(
          'mouseleave',
          this.resumeHandler
        );
      }

      this.marqueeResizeObserver?.disconnect();
      cancelAnimationFrame(this.marqueeFrame);
    }

    bindEvents() {
      if (this.links.length) {
        window.addEventListener(
          'on:breakpoint-change',
          this.moveLinksHandler
        );
      }

      if (this.localization) {
        window.addEventListener(
          'on:breakpoint-change',
          this.moveLocalizationHandler
        );
      }

      if (this.slider) {
        this.slider.addEventListener(
          'mouseenter',
          this.pauseHandler
        );

        this.slider.addEventListener(
          'mouseleave',
          this.resumeHandler
        );
      }
    }

    init() {
      if (this.slider) {
        this.initMarquee();
      }

      // 保持原来的设置：首次加载不移动帮助链接
      // this.moveLinks();

      if (this.localization) {
        this.moveLocalization();
      }
    }

    /**
     * Create the continuous marquee.
     */
    initMarquee() {
      if (!this.marqueeTrack) {
        const slides = Array.from(
          this.slider.querySelectorAll('.announcement__text')
        );

        if (!slides.length) return;

        const track = document.createElement('div');
        track.className = 'announcement__marquee-track';

        const group = document.createElement('div');
        group.className = 'announcement__marquee-group';

        slides.forEach((slide) => {
          slide.classList.remove('is-visible');
          group.appendChild(slide);
        });

        track.appendChild(group);
        this.slider.appendChild(track);

        this.marqueeTrack = track;
        this.marqueeGroup = group;
        this.marqueeSlides = slides;
      }

      this.slider.scrollLeft = 0;
      this.lastMarqueeWidth = null;

      this.marqueeResizeObserver?.disconnect();

      this.marqueeResizeObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width;

        // 只有窗口宽度变化时才重建，避免反复重启动画
        if (width !== this.lastMarqueeWidth) {
          this.lastMarqueeWidth = width;
          this.scheduleMarqueeRebuild();
        }
      });

      this.marqueeResizeObserver.observe(this.slider);
      this.scheduleMarqueeRebuild();

      if (document.fonts) {
        document.fonts.ready.then(() => {
          if (this.isConnected) {
            this.scheduleMarqueeRebuild();
          }
        });
      }
    }

    scheduleMarqueeRebuild() {
      cancelAnimationFrame(this.marqueeFrame);

      this.marqueeFrame = requestAnimationFrame(() => {
        this.rebuildMarquee();
      });
    }

    /**
     * Create a visual duplicate without duplicate IDs or tab stops.
     */
    cloneForMarquee(element) {
      const clone = element.cloneNode(true);

      clone.setAttribute('aria-hidden', 'true');

      [clone, ...clone.querySelectorAll('*')].forEach((el) => {
        el.removeAttribute('id');
        el.removeAttribute('data-shopify-editor-block');

        if (
          el.matches(
            'a, button, input, select, textarea, [tabindex]'
          )
        ) {
          el.setAttribute('tabindex', '-1');
        }
      });

      return clone;
    }

    /**
     * Fill the viewport and duplicate a complete group.
     */
    rebuildMarquee() {
      if (
        !this.isConnected ||
        !this.slider ||
        !this.marqueeTrack ||
        !this.marqueeGroup
      ) {
        return;
      }

      const track = this.marqueeTrack;
      const group = this.marqueeGroup;
      const slides = this.marqueeSlides;

      // Stop animation while measuring
      track.style.animation = 'none';

      // Remove previously added copies
      Array.from(group.children).forEach((child) => {
        if (!slides.includes(child)) {
          child.remove();
        }
      });

      Array.from(track.children).forEach((child) => {
        if (child !== group) {
          child.remove();
        }
      });

      const viewportWidth = this.slider.clientWidth;
      const originalWidth = group.getBoundingClientRect().width;

      // ResizeObserver will retry when the element becomes visible
      if (!viewportWidth || !originalWidth) return;

      const repeatCount = Math.max(
        1,
        Math.ceil(viewportWidth / originalWidth)
      );

      // Extend the first group to cover the viewport
      for (let i = 1; i < repeatCount; i += 1) {
        slides.forEach((slide) => {
          group.appendChild(this.cloneForMarquee(slide));
        });
      }

      // Duplicate the complete first group
      track.appendChild(this.cloneForMarquee(group));

      const distance = group.getBoundingClientRect().width;

      track.style.setProperty(
        '--announcement-marquee-distance',
        `${distance}px`
      );

      const readTime = Number(this.dataset.slideDelay) || 7000;
      const speed = 210 / Math.max(3, readTime / 1000);
      const duration = distance / speed;

      // Flush the stopped state before restarting
      void track.offsetWidth;

      // Remove shorthand FIRST, then set animation properties
      track.style.removeProperty('animation');
      track.style.animationName = 'announcement-marquee';
      track.style.animationDuration = `${duration}s`;
      track.style.animationTimingFunction = 'linear';
      track.style.animationIterationCount = 'infinite';
      track.style.animationPlayState =
        this.isPaused ? 'paused' : 'running';
    }

    /**
     * Move help links into the mobile menu.
     */
    moveLinks() {
      const menuAnnouncementLinks = document.querySelector(
        '.mob__announcement-links'
      );

      this.menu = document.querySelector('.main-menu__content');

      if (!theme.mediaMatches.md && !menuAnnouncementLinks) {
        if (!this.menu || !this.links.length) return;

        const mobNav = document.createElement('nav');
        mobNav.classList.add(
          'mob__announcement-links',
          'secondary-nav'
        );

        const list = document.createElement('ul');

        this.links.forEach((link) => {
          const item = document.createElement('li');
          const mobileLink = document.createElement('a');

          mobileLink.className = 'secondary-nav__item';
          mobileLink.href = link.href;
          mobileLink.textContent = link.innerText;

          item.appendChild(mobileLink);
          list.appendChild(item);
        });

        mobNav.appendChild(list);
        this.menu.appendChild(mobNav);
      } else if (theme.mediaMatches.md && menuAnnouncementLinks) {
        menuAnnouncementLinks.remove();
      }
    }

    /**
     * Move localization into the mobile menu or back to the bar.
     */
    moveLocalization() {
      this.localization = this.querySelector(
        '.announcement__localization'
      );
      this.menu = document.querySelector('.main-menu__content');

      if (!this.localization) return;

      const menuLocalization = document.querySelector(
        '.mob__localization'
      );

      if (!theme.mediaMatches.md && !menuLocalization) {
        const form = this.localization.firstElementChild;

        if (!form || !this.menu) return;

        const mobileLocalization = document.createElement('div');
        mobileLocalization.classList.add('mob__localization');
        mobileLocalization.appendChild(form);

        this.menu.appendChild(mobileLocalization);
      } else if (theme.mediaMatches.md && menuLocalization) {
        const form = menuLocalization.firstElementChild;

        if (form) {
          this.localization.appendChild(form);
        }

        menuLocalization.remove();
      }
    }

    pauseSlider() {
      this.isPaused = true;

      if (this.marqueeTrack) {
        this.marqueeTrack.style.animationPlayState = 'paused';
      }
    }

    resumeSlider() {
      this.isPaused = false;

      if (this.marqueeTrack) {
        this.marqueeTrack.style.animationPlayState = 'running';
      }
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}